import getSuggestedTransaction from 'api/v1/transactions/getSuggestedTransaction';
import classNames from 'classnames';
import { Button } from 'components/atoms/Button';
import useSuperQuery from 'hooks/useSuperQuery';
import { FC, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import useQueryClient from 'hooks/react-query/useClientQuery';

import { OtherCacheKeys } from 'types/cacheKeys.types';
import { Currency } from 'types/global.types';
import { Transaction } from 'types/transactions.types';
import { TracingConfig } from 'utils/amplitude/amplitude.types';
import { CheckIconOutline, XIconOutline } from 'utils/icons';
import { ONE_SECOND } from 'utils/time';
import transactionToOption from 'utils/transactionToOption';
import { TransactionOption } from './utils';

type Props = {
  data: Partial<{
    amount: number;
    currency: Currency;
    date: string;
    communication: string | null;
    name: string;
    IBAN: string | null;
    documentNumber: string | number;
  }>;
  isDisabled?: boolean;
  className?: string;
  wrapperClassName?: string;
  id?: string;
  onAccept: (transaction: Transaction) => void;
  onDecline?: () => void;
  onSuggestionChange?: (transaction: Transaction | null) => void;
  tracing?: TracingConfig<'onAccept' | 'onDecline'>;
};

const SuggestedTransaction: FC<Props> = ({
  data,
  isDisabled,
  className,
  wrapperClassName,
  onAccept,
  onDecline,
  onSuggestionChange,
  tracing,
  id,
}) => {
  const { t } = useTranslation();

  const queryClient = useQueryClient();

  const queryKey = [OtherCacheKeys.SuggestedTransaction, data, isDisabled];

  const { data: suggestedTransaction } = useSuperQuery({
    queryKey,
    queryFn: () =>
      getSuggestedTransaction(data).then(
        (transaction) => transaction && transactionToOption(transaction),
      ),
    enabled: !isDisabled && !!data.date,
    gcTime: 0,
    staleTime: 0,
    debounceTime: ONE_SECOND / 2,
  });

  const nullify = () => {
    queryClient.setQueryData(queryKey, null);
  };

  const _onChangeRef = useRef(onSuggestionChange);
  _onChangeRef.current = onSuggestionChange;

  useEffect(() => {
    _onChangeRef.current?.(suggestedTransaction?.transaction || null);
  }, [suggestedTransaction]);

  if (!suggestedTransaction) return null;

  return (
    <div
      id={id}
      className={classNames(wrapperClassName, 'flex flex-col gap-3')}
    >
      <div
        className={classNames(
          className,
          'px-4 py-3.5 rounded-md text-main flex gap-6 ai-guess-tag-bg-border',
        )}
      >
        <TransactionOption
          transaction={suggestedTransaction.transaction}
          className="flex-1"
        />
      </div>
      <div className="flex gap-3 flex-1">
        <Button
          className="flex-1 gap-1"
          color="gray"
          tracingEvent={tracing?.onDecline}
          onClick={() => {
            nullify();
            onDecline?.();
          }}
        >
          <XIconOutline className="w-5" />
          <span>{t('suggested_transaction.reject')}</span>
        </Button>
        <Button
          className="flex-1 gap-1"
          tracingEvent={tracing?.onAccept}
          onClick={() => onAccept(suggestedTransaction.transaction)}
        >
          <CheckIconOutline className="w-5" />
          <span>{t('suggested_transaction.accept')}</span>
        </Button>
      </div>
    </div>
  );
};

export default SuggestedTransaction;
